import { useContext } from "react";
import useTranslation from "next-translate/useTranslation";
import { ThemeContext } from "styled-components";
import Heading from "../common/Heading";
import Button from "../common/Button";
import ServiceIcon from "../common/ServiceIcon";
import { Box, Flex, Text } from "rebass/styled-components";
import { joinStrings, joinAndFormatDates } from "../../lib/utils";
import { useIsMobile } from "../../lib/media";
import { trackEvent } from "../../lib/event-tracking";

import {
  useOwnerDeclineJobApplicationMutation,
  useOwnerAcceptJobApplicationMutation,
  GetOwnerJobsForConversationsDocument
} from "../../generated/graphql";

interface Props {
  jobApplicationDetails: any; // TODO FIX
  modalView?: boolean;
  onAction?: (conversationId?: string) => void;
}

const JobActions = ({ jobApplicationDetails, modalView, onAction }: Props) => {
  const { t } = useTranslation("conversations");
  const theme = useContext(ThemeContext);
  const isMobile = useIsMobile();

  const {
    jobMatchId,
    dogs,
    startDate,
    endDate,
    serviceDates,
    datesRequested,
    sitterId,
    sitterName,
    serviceTypeId,
    paymentTotal
  } = jobApplicationDetails;

  const dogNames = joinStrings(dogs.map(d => d.name));
  const datesS = joinAndFormatDates(startDate, endDate, serviceDates);

  const [
    declineApplication,
    { loading: declining }
  ] = useOwnerDeclineJobApplicationMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetOwnerJobsForConversationsDocument }]
  });

  const [
    acceptApplication,
    { loading: accepting }
  ] = useOwnerAcceptJobApplicationMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetOwnerJobsForConversationsDocument }]
  });

  const decline = async () => {
    if (accepting || declining) return;

    await declineApplication({
      variables: { input: { jobMatchId } }
    });

    trackEvent("owner_job_application_declined", { jobMatchId });

    onAction && onAction();
  };

  const accept = async () => {
    if (accepting || declining) return;

    const result = await acceptApplication({
      variables: { input: { jobMatchId } }
    });

    const conversationId =
      result.data?.ownerAcceptJobApplication?.conversation?.id || undefined;

    trackEvent("owner_job_application_accepted", { jobMatchId });

    onAction && onAction(conversationId);
  };

  return (
    <Box
      sx={{
        top: ["initial", modalView ? "24px" : "155px"],
        right: 0,
        bottom: 0,
        left: 0,
        position: "sticky",
        zIndex: 1,
        width: modalView && isMobile ? "100vw" : "100%",
        marginLeft: modalView && isMobile ? "-24px" : 0
      }}
    >
      {!isMobile && (
        <Box>
          <Heading
            as="h3"
            lineHeight="heading"
            fontSize={5}
            fontWeight="book"
            mb={1}
          >
            {t("jobApplicantTitle", { sitter: sitterName })}
          </Heading>

          <Text mb={2}>
            {t("jobApplicantDescription", {
              sitter: sitterName,
              dogs: dogNames,
              dates: datesS
            })}
          </Text>
        </Box>
      )}

      <Box
        p={[2, 3]}
        sx={{
          zIndex: 1,
          border: ["none", `1px solid ${theme["colors"].gray}`],
          borderTop: `1px solid ${theme["colors"].gray}`,
          borderRadius: [0, "default"],
          backgroundColor: `${theme["colors"].samoyedWhite}`
        }}
      >
        <Flex
          pb={2}
          mb={2}
          justifyContent="space-between"
          sx={{ borderBottom: "1px solid lightgray" }}
        >
          <Flex mr={2}>
            <Box
              mr={3}
              sx={{
                flex: "none"
              }}
            >
              <ServiceIcon
                fontSize={7}
                serviceTypeId={jobApplicationDetails.serviceTypeId}
                color="springGreen"
              />
            </Box>
            <Box pt={"2px"}>
              <Heading as="h4" fontSize={2} fontWeight="bold">
                {t(`serviceType${serviceTypeId}`)}
              </Heading>
              <Text as="p" color="darkGray">
                {t("serviceDescription", {
                  dogsCount: dogs.length,
                  datesCount: datesRequested
                })}
              </Text>
            </Box>
          </Flex>

          <Box pt={"2px"}>
            <Heading as="h4" fontSize={2} fontWeight="bold">
              {paymentTotal}
            </Heading>
            <Text as="p" color="darkGray">
              {t("paymentTotalInfo")}
            </Text>
          </Box>
        </Flex>

        <Flex justifyContent="space-between">
          <Button
            mr={2}
            width={1}
            variant="terciary"
            size="large"
            onClick={decline}
          >
            {t("reject")}
          </Button>
          <Button width={1} variant="primary" size="large" onClick={accept}>
            {t("contact")}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

JobActions.defaultProps = {
  queryParams: null
};

export default JobActions;
